/// <amd-module name="Release/Landing/LeftPanel/ActivePipelines/ActivePipelineList" />
import * as React from "react";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Table, ITableColumn, TableColumnStyle, ITableRow, TableCell } from "VSSUI/Table";
import { ListSelection, IListSelection } from "VSSUI/List";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { VssComponent, IVssComponentProperties } from "VSS/Platform/Layout";

import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ReleaseDefinition, Deployment, ReleaseApproval } from "Release/Client/Contracts/Release";
import { IActiveDefinitionReference } from "Release/Landing/DataProviderContracts";
import { ActivePipelineRow } from "Release/Landing/LeftPanel/ActivePipelines/ActivePipelineRow";
import * as Resources from "Release/Landing/Resources";
import { SelectionStore } from "Release/Landing/Selection/SelectionStore";

export interface IActivePipelinesListProps extends IVssComponentProperties {
    activeDefinitions: IActiveDefinitionReference[] | null;
    recentDefinitions: IActiveDefinitionReference[] | null;
    searchText: string;
    searchedDefinitions: ReleaseDefinition[] | null;
    searchResultsLoading: boolean;
    onRowClick: (definitionId: number, definitionName: string) => void;
}

const ActivePipelinesGroupKey = "active-pipelines-group";
const RecentPipelinesGroupKey = "recent-pipelines-group";
const SearchedPipelinesGroupKey = "searched-pipelines-group";

interface IActivePipelineItem {
    id: number;
    name: string;
    isSearched: boolean;
    lastDeployment: Deployment | null | undefined;
    pendingApproval: ReleaseApproval | null | undefined;
}

export class ActivePipelineList extends VssComponent<IActivePipelinesListProps, {}>{
    constructor(props: IActivePipelinesListProps) {
        super(props);
        if (!this._selection) {
            this._selection = new ListSelection();
            const selectedItemId = StoreManager.GetStore<SelectionStore>(SelectionStore).getState().id;
            const selectedIndex = [...(props.activeDefinitions || []), ...(props.recentDefinitions || [])].findIndex(d => d.id === selectedItemId);
            if (selectedIndex >= 0) {
                this._selection.select(selectedIndex);
            }
        }
    }

    public render(): React.ReactNode {

        if (!this.props.activeDefinitions && !this.props.recentDefinitions) {
            return <Spinner className={"flex flex-grow loading-active"} size={SpinnerSize.large} />;
        }

        if (this.props.searchText) {
            if (this.props.searchResultsLoading) {
                return <Spinner className={"flex flex-grow loading-active"} size={SpinnerSize.large} />;
            }
            else if (this.props.searchedDefinitions && this.props.searchedDefinitions.length === 0) {
                return (
                    <MessageBar
                        className={"active-pivot-no-definitions-found-message"}
                        severity={MessageBarSeverity.Info}>
                        {Resources.NoDefinitionsMatchYourFilterText}
                    </MessageBar>
                );
            }
        }

        const getItemFromDefinitionReference = (reference: IActiveDefinitionReference): IActivePipelineItem => {
            return {
                id: reference.id,
                isSearched: false,
                lastDeployment: reference.lastDeployment,
                name: reference.name,
                pendingApproval: reference.pendingApproval
            };
        };

        const getItemFromReleaseDefinition = (rd: ReleaseDefinition): IActivePipelineItem => {
            return {
                id: rd.id,
                isSearched: true,
                lastDeployment: null,
                name: rd.name,
                pendingApproval: null
            };
        };

        const activePipelines = this.props.activeDefinitions || [];
        const recentPipelines = this.props.recentDefinitions || [];
        const allPipelines: IActivePipelineItem[] = [...activePipelines, ...recentPipelines].map(getItemFromDefinitionReference);

        const searchedPipelines: IActivePipelineItem[] = (this.props.searchedDefinitions || []).map(getItemFromReleaseDefinition);

        return (
            <Table
                className={"active-pipelines-table"}
                itemProvider={new ArrayItemProvider(this.props.searchText ? searchedPipelines : allPipelines)}
                columns={[{ width: -100, id: "rel-pipe-def-col", renderCell: this._renderTableCell, columnStyle: TableColumnStyle.Secondary }]}
                showLines={false}
                selection={this._selection}
                onActivate={(event, row: ITableRow<IActivePipelineItem>) => this.props.onRowClick(row.data.id, row.data.name)}
                pageSize={30}
            />
        )
    }

    public componentDidUpdate(prevProps: IActivePipelinesListProps): void {
        if (!!this.props.searchText && !prevProps.searchText) {
            this.setTimeout(() => this._selection.clear(), 0);
        }
    }

    private _renderTableCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IActivePipelineItem>, tableItem: IActivePipelineItem): JSX.Element => {
        const activePipelinesLength = (this.props.activeDefinitions || []).length;
        const className = (!this.props.searchText && activePipelinesLength === rowIndex + 1) ? "last-active" : ""

        return (
            <TableCell
                key={"pipeline" + tableItem.id}
                className={className}
                columnIndex={columnIndex}
                tableColumn={tableColumn}>

                {this._getCell(tableItem)}

            </TableCell>);
    }

    private _getCell(item: IActivePipelineItem): JSX.Element {
        return (<ActivePipelineRow
            name={item.name}
            definitionId={item.id}
            lastDeployment={item.lastDeployment}
            pendingApproval={item.pendingApproval}
            isSearched={item.isSearched}
            onClick={() => this.props.onRowClick(item.id, item.name)}
        />);
    }

    private _selection: IListSelection;
}
